import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { useStoreActions, useStoreState } from "easy-peasy";
import EditableTable from "lib/Tables/EditableTable";
import { formatCurrencyUSD } from "utils/CommonUtils";

interface FeesHoldbacksProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const FeesHoldbacks: React.FC<FeesHoldbacksProps> = ({
  dealApplicationId,
  setComponentLoading,
}) => {
  const onInit = useStoreActions((actions: any) => actions.heldBackCommissionStore.onInit);
  const heldbackRefresh = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.refresh
  );

  const onContractTermsInit = useStoreActions((actions: any) => actions.offerStore.onInit);

  const saveHeldback = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.saveHeldback
  );

  const heldbacksData = useStoreState((state: any) => state.heldBackCommissionStore.heldbacks);
  const deleteHeldBack = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.deleteCommission
  );

  const brokersList = useStoreState((state: any) => state.heldBackCommissionStore.brokersList);
  const heldbackTypes = useStoreState(
    (state: any) => state.heldBackCommissionStore.transactionTypes
  );
  const fetchRecalculatedApplication = useStoreActions(
    (actions: any) => actions.offerStore.fetchRecalculatedApplication
  );
  const getBrokersList = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.getBrokersList
  );

  const brokersOptions = brokersList?.map((broker: any) => {
    return {
      label: broker?.name,
      value: broker?.id,
    };
  });

  const formatLabel = (label: any) => {
    if (!label) return "";

    return label
      .replace(/_/g, " ")
      .split(" ")
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const heldbackOptions = heldbackTypes?.map((heldback: any) => {
    return {
      label: formatLabel(heldback?.transactionTypeName),
      value: heldback?.transactionTypeName,
    };
  });

  const [feesData, setFeesData] = useState([]);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<string | null>("amount");
  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);

  const refresh = async (dealApplicationId: any) => {
    await heldbackRefresh(dealApplicationId);
    await fetchRecalculatedApplication(dealApplicationId);
    await onContractTermsInit(dealApplicationId);
    getBrokersList();
  };

  useEffect(() => {
    try {
      setComponentLoading("feesHoldbacks", true);
      onInit(dealApplicationId);
    } finally {
      setComponentLoading("feesHoldbacks", false);
    }
  }, []);

  useEffect(() => {
    setFeesData(heldbacksData);
  }, [heldbacksData]);

  const renderTotal = (key: any) => {
    let total = 0;

    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }

    feesData
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("amount") ? formatCurrencyUSD(total.toFixed(2)) : total.toFixed(2);
  };

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const handleDelete = async (id: any) => {
    console.log("id", id);
    await deleteHeldBack({
      id: id,
      type: "",
      applicationId: dealApplicationId,
      deletingIds: [],
      callback: async () => {
        await refresh(dealApplicationId);
      },
    });
  };

  const columns = [
    {
      Header: "Distributions",
      accessor: "applicationPartyName",
      selector: "applicationPartyId",
      align: "left",
      width: "350px",
      input: "customSelect",
      options: brokersOptions,
    },
    {
      Header: "Fee Type",
      accessor: "type",
      align: "left",
      input: "select",
      options: heldbackOptions,
      Cell: (row: any) => formatLabel(row?.row?.type),
    },
    {
      Header: "Amount",
      accessor: "amount",
      align: "left",
      input: "amount",
      total: true,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.amount),
    },
    {
      Header: "Description",
      accessor: "description",
      required: false,
      align: "left",
    },
  ];

  const onSave = async () => {
    try {
      setLoading(true);
      console.log("editing row", editingRow);
      const type = editingRow?.type;

      const request: any = {
        amount: editingRow.amount,
        applicationId: editingRow.applicationId,
        description: editingRow.description || "",
        name: editingRow.applicationPartyName,
        partyId: editingRow.applicationPartyId,
        tenantId: editingRow.tenantId,
        type: type,
      };

      if (editingRow?.newBrokerParty) {
        request.newBrokerParty = editingRow?.newBrokerParty;
      }

      if (editingRow.id) {
        request.id = editingRow.id;
      }

      if (editingRow.oldAmount) {
        request.oldAmount = editingRow.oldAmount;
      }

      if (editingRow.isInvested) {
        request.isInvested = editingRow.isInvested;
      }

      if (
        editingRow.applicationPartyId &&
        editingRow.oldPartyId &&
        editingRow.applicationPartyId !== editingRow.oldPartyId
      ) {
        request.oldPartyId = editingRow.oldPartyId;
      }

      const offerDetails = await onContractTermsInit(dealApplicationId);
      await saveHeldback({
        offerDetails: offerDetails,
        heldbackData: request,
        dealApplicationId: dealApplicationId,
        callback: async () => {
          await refresh(dealApplicationId);
        },
      });
    } finally {
      setEditingRow(null);
      setIsEditing(false);
      setLoading(false);
    }
  };

  const handleEditHeldback = (row: any | null) => {
    if (row.amount) {
      row.oldAmount = row.amount;
    }

    if (row.type) {
      row.originalType = row.type;
    }

    if (row.applicationPartyId) {
      row.oldPartyId = row.applicationPartyId;
    }

    setIsEditing(true);
    setEditingRow(row ? { ...row } : {});
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setIsEditing(false);
  };

  const handleInputChange = (value: string, field: string) => {
    let updatedRow = { ...editingRow };

    if (field === "addNew") {
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      updatedRow["showAddingNew"] = true;
      setEditingRow(updatedRow);
      return;
    }

    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddNewBroker = (value: string): boolean => {
    let updatedRow = { ...editingRow };

    if (!value) {
      updatedRow["showAddingNew"] = false;
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      setEditingRow(updatedRow);
      return true;
    }

    const nameExists = brokersList?.some(
      (broker: any) => broker?.name?.toLowerCase() === value.toLowerCase()
    );

    if (nameExists) {
      return false;
    }

    updatedRow["newBrokerParty"] = value;
    updatedRow["addNewHeldbackBrokerParty"] = true;
    updatedRow["applicationPartyName"] = null;
    updatedRow["applicationPartyId"] = null;
    setEditingRow(updatedRow);

    return true;
  };

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === feesData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(feesData.map((row: any) => row.id));
    }
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const isActionsDisabled = (row: any | null) => {
    return row.defaultSyndicator && row.type === "bank_fee";
  };

  const getRowStyle = (row: any) => {
    return "regular";
  };

  const handleAddHeldback = () => {
    const newHeldback: any = {
      applicationPartyName: undefined,
      partyId: undefined,
      type: "",
      amount: 0,
      description: "",
    };

    setEditingRow(newHeldback);
    setIsEditing(true);
  };

  const customButton = (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddHeldback}
    >
      + Add Fee
    </MDButton>
  );

  const renderTable = () => {
    return (
      <EditableTable
        rowKey={"id"}
        columns={columns}
        data={heldbacksData}
        onDelete={handleDelete}
        onEdit={handleEditHeldback}
        onSave={onSave}
        onCancel={handleCancelEdit}
        editingRow={editingRow}
        isEditing={isEditing}
        onInputChange={handleInputChange}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        getRowStyle={getRowStyle}
        isActionsDisabled={isActionsDisabled}
        renderTotal={renderTotal}
        totalColumn={["amount"]}
        onAddNewSubmit={handleAddNewBroker}
      />
    );
  };

  return (
    <CollapsibleCard header={"Fees/Holdbacks"} customButton={customButton}>
      <MDBox paddingRight={3}>{renderTable()}</MDBox>
    </CollapsibleCard>
  );
};

export default FeesHoldbacks;
